/**
 * Utility functions for handling images across the application
 */

// Default fallback image - cached locally to avoid network requests
const FALLBACK_IMAGE = 'https://raw.githubusercontent.com/NiroWeb/logo/refs/heads/main/efootball-2025-logo-white-260x300.webp';

// URL patterns for migration support
const OLD_URL_PATTERN = /https:\/\/efootballstats\.com\/card\/f(\d+)\.png/;
const PESDB_PATTERN = /https:\/\/pesdb\.net\/assets\/img\/card\/f(\d+)\.png/;
const EFOOTBALLHUB_URL_TEMPLATE = 'https://efootballhub.net/images/efootball24/players/{playerId}_l.webp';

/**
 * Converts old image URL format to new format
 * @param url The original image URL
 * @returns The converted URL or original URL if no conversion needed
 */
export function convertLegacyImageUrl(url: string): string {
  // Convert old efootballstats.com URLs to efootballhub.net
  const oldMatch = url.match(OLD_URL_PATTERN);
  if (oldMatch) {
    const playerId = oldMatch[1];
    return EFOOTBALLHUB_URL_TEMPLATE.replace('{playerId}', playerId);
  }

  // Convert pesdb.net URLs to efootballhub.net (pesdb has hotlink protection)
  const pesdbMatch = url.match(PESDB_PATTERN);
  if (pesdbMatch) {
    const playerId = pesdbMatch[1];
    return EFOOTBALLHUB_URL_TEMPLATE.replace('{playerId}', playerId);
  }

  return url;
}

/**
 * Validates if an image URL is accessible
 * @param url The image URL to validate
 * @returns Promise that resolves to true if accessible, false otherwise
 */
async function validateImageUrl(url: string): Promise<boolean> {
  try {
    const response = await fetch(url, { method: 'HEAD' });
    return response.ok;
  } catch {
    return false;
  }
}


/**
 * Gets a proxied image URL with WebP/AVIF conversion for external images
 * Falls back to a default image if no URL is provided
 * Automatically converts legacy URLs to new format
 *
 * @param url The original image URL
 * @param quality Optional WebP quality (1-100), defaults to 85
 * @param width Optional width to resize the image
 * @param format Optional format (webp, avif, auto) to convert the image to
 * @returns The proxied image URL with proper conversion
 */
export function getProxiedImageUrl(
  url?: string,
  quality: number = 85,
  width?: number,
  format: 'webp' | 'avif' | 'auto' = 'webp'
): string {
  if (!url) return FALLBACK_IMAGE;

  // Convert legacy URLs to new format
  url = convertLegacyImageUrl(url);

  // For mobile optimization: if width is small (<=400px), prefer 'm' prefix for better performance
  // For larger widths, convert 'm' to 'f' for better quality
  // Note: pesdb.net URLs don't use this pattern, so this only applies to legacy URLs
  if (url.includes('/card/m') && width && width > 400) {
    url = url.replace('/card/m', '/card/f');
  } else if (url.includes('/card/f') && width && width <= 400) {
    // For small widths, prefer mobile version if available
    url = url.replace('/card/f', '/card/m');
  }

  // Safe URL parsing with error handling
  let urlDomain = '';
  let isTrustedDomain = false;

  try {
    const urlObj = new URL(url);
    urlDomain = urlObj.hostname;
    const trustedDomains = ['localhost', '127.0.0.1', window.location.hostname];
    isTrustedDomain = trustedDomains.includes(urlDomain);
  } catch (e) {
    return FALLBACK_IMAGE;
  }

  if (isTrustedDomain) {
    if (format === 'webp' && url.toLowerCase().endsWith('.webp')) {
      return url;
    }

    if (format === 'avif' && url.toLowerCase().endsWith('.avif')) {
      return url;
    }
  }

  // Check if it's an external URL (not a local asset path)
  if (url.startsWith('http') || url.startsWith('https')) {
    // Firebase Storage URLs - load directly (our own hosting, no CORS issues)
    if (url.includes('storage.googleapis.com') || url.includes('firebasestorage.googleapis.com')) {
      return url;
    }
    // pesdb.net has hotlink protection that returns watermarked placeholder images
    // Return fallback image instead of watermarked placeholder
    if (url.includes('pesdb.net')) {
      return FALLBACK_IMAGE;
    }
    // Use proxy for other external URLs
    return `https://wsrv.nl/?url=${encodeURIComponent(url)}&default=noimage`;
  }

  // For local assets, return as is
  return url;
}

/**
 * Handles image loading errors by setting a fallback image
 * Attempts to convert legacy URLs before falling back to default
 *
 * @param event The error event
 */
export function handleImageError(event: React.SyntheticEvent<HTMLImageElement, Event>): void {
  const img = event.currentTarget;
  const originalSrc = img.src;

  // If this is already the fallback image, don't try again
  if (originalSrc === FALLBACK_IMAGE) {
    return;
  }
  
  // Try to extract the original URL from a proxy URL
  let baseUrl = originalSrc;
  const urlMatch = originalSrc.match(/url=([^&]+)/);
  if (urlMatch) {
    try {
      baseUrl = decodeURIComponent(urlMatch[1]);
    } catch (e) {
      // Failed to decode URL, continue with original
    }
  }

  // In development, try the direct URL without any proxy
  if ((window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') &&
      !originalSrc.includes('githubusercontent.com') &&
      baseUrl !== originalSrc) {
    img.src = baseUrl;

    const directHandler = () => {
      img.src = FALLBACK_IMAGE;
      img.removeEventListener('error', directHandler);
    };
    img.addEventListener('error', directHandler, { once: true });
    return;
  }

  // Use fallback immediately in all other cases
  img.src = FALLBACK_IMAGE;
}

/**
 * Enhanced image loading with retry logic and URL conversion
 * @param url The image URL to load
 * @param retries Number of retry attempts (default: 2)
 * @returns Promise that resolves when image loads successfully
 */
export async function loadImageWithRetry(url: string, retries: number = 2): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    let attemptCount = 0;
    
    const tryLoad = (urlToTry: string) => {
      attemptCount++;
      
      img.onload = () => {
        resolve(urlToTry);
      };
      
      img.onerror = () => {
        if (attemptCount === 1) {
          // First attempt failed, try converted URL if applicable
          const convertedUrl = convertLegacyImageUrl(urlToTry);
          if (convertedUrl !== urlToTry) {
            tryLoad(convertedUrl);
            return;
          }
        }

        if (attemptCount < retries + 1) {
          // Retry with same URL after a delay
          setTimeout(() => tryLoad(urlToTry), 1000 * attemptCount);
        } else {
          // All retries exhausted, use fallback
          resolve(FALLBACK_IMAGE);
        }
      };
      
      img.src = urlToTry;
    };
    
    tryLoad(url);
  });
}

/**
 * Determines if the device is on a slow connection
 * 
 * @returns True if the device is on a slow connection
 */
export function isSlowConnection(): boolean {
  if (typeof navigator === 'undefined') return false;
  
  const connection = (navigator as any).connection;
  
  if (!connection) return false;
  
  // Check if the user has enabled data-saving mode
  if (connection.saveData) return true;
  
  // Check connection type
  const slowConnections = ['slow-2g', '2g', '3g'];
  if (connection.effectiveType && slowConnections.includes(connection.effectiveType)) {
    return true;
  }
  
  // Check connection speed if available (in Mbps)
  if (connection.downlink && connection.downlink < 1) {
    return true;
  }
  
  return false;
}

/**
 * Prepares image sources for responsive loading
 * Generates different sizes for different viewport widths
 * Automatically converts legacy URLs to new format
 *
 * @param url The original image URL
 * @param isPriority Whether this is a high-priority image (affects quality settings)
 * @returns Object with srcset and sizes for responsive images
 */
export function getResponsiveImageSrc(url?: string, isPriority: boolean = false): { src: string, srcSet: string, sizes: string } {
  if (!url) {
    return {
      src: FALLBACK_IMAGE,
      srcSet: '',
      sizes: ''
    };
  }
  
  // Convert legacy URLs to new format
  url = convertLegacyImageUrl(url);
  
  // Convert any small image URLs (m prefix) to full-size image URLs (f prefix)
  if (url.includes('/card/m')) {
    url = url.replace('/card/m', '/card/f');
  }
  
  // For local assets (non-HTTP URLs), return simple object
  if (!(url.startsWith('http') || url.startsWith('https'))) {
    return {
      src: url,
      srcSet: '',
      sizes: ''
    };
  }
  
  // For trusted domains and Firebase Storage, return simple object without proxying
  // Firebase Storage is our own hosting
  const trustedDomains = ['localhost', '127.0.0.1', window.location.hostname];
  const trustedPatterns = ['storage.googleapis.com', 'firebasestorage.googleapis.com'];
  try {
    const urlObj = new URL(url);

    // pesdb.net has hotlink protection - return fallback image
    if (urlObj.hostname.includes('pesdb.net')) {
      return {
        src: FALLBACK_IMAGE,
        srcSet: '',
        sizes: ''
      };
    }

    const isTrusted = trustedDomains.includes(urlObj.hostname) ||
                      trustedPatterns.some(pattern => urlObj.hostname.includes(pattern));
    if (isTrusted) {
      return {
        src: url,
        srcSet: '',
        sizes: ''
      };
    }
  } catch (e) {
    // If URL parsing fails, continue with proxying
  }
  
  // Adjust quality based on connection speed
  const isOnSlowConnection = isSlowConnection();
  
  // For high-priority images, use higher quality but smaller dimensions to load faster
  // For slow connections, reduce quality
  const highPriorityQuality = isPriority ? 90 : 85;
  const normalQuality = isOnSlowConnection ? 70 : 80;
  const lowQuality = isOnSlowConnection ? 60 : 75;
  
  // Use AVIF for better compression if possible
  const format = 'auto';
  
  // Create responsive image sources with different widths
  // For priority images, we optimize for faster loading with slightly smaller initial sizes
  // Maintain the 240:340 aspect ratio for all sizes
  const smallWidth = isPriority ? 250 : 300;
  const mediumWidth = isPriority ? 500 : 600; 
  const largeWidth = 900;
  
  // Calculate heights based on 240:340 aspect ratio
  const smallHeight = Math.round(smallWidth * (340 / 240));
  const mediumHeight = Math.round(mediumWidth * (340 / 240));
  const largeHeight = Math.round(largeWidth * (340 / 240));
  
  const small = getProxiedImageUrl(url, highPriorityQuality, smallWidth, format);
  const medium = getProxiedImageUrl(url, normalQuality, mediumWidth, format);
  const large = getProxiedImageUrl(url, lowQuality, largeWidth, format);
  
  return {
    // For priority images, use the small size as the src for faster initial load
    src: isPriority ? small : medium,
    srcSet: `${small} 300w, ${medium} 600w, ${large} 900w`,
    sizes: '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
  };
}

/**
 * Preloads images for the adjacent pages (next/previous) to improve performance
 * Only preloads a subset of images to avoid excessive bandwidth usage
 * 
 * @param players The array of all players
 * @param currentPage The current page index
 * @param pageSize The number of players per page
 */
export function preloadAdjacentPageImages(
  players: Array<{ image?: string; image_small?: string; image_front?: string; }>,
  currentPage: number,
  pageSize: number
): void {
  // Don't preload on slow connections
  if (isSlowConnection()) return;
  
  // Calculate start index for the next page
  const nextPageStart = (currentPage) * pageSize;
  const prevPageStart = Math.max(0, (currentPage - 2) * pageSize);
  
  // Only preload if there are more pages
  const hasNextPage = nextPageStart < players.length;
  const hasPrevPage = currentPage > 1;
  
  // Determine which images to preload
  const imagesToPreload: string[] = [];
  
  // For next page, preload first 6 images with low quality
  if (hasNextPage) {
    const nextPagePlayers = players.slice(nextPageStart, nextPageStart + Math.min(6, pageSize));
    nextPagePlayers.forEach(player => {
      // Choose the most appropriate image
      let imageUrl = player.image_front || player.image || player.image_small;
      
      if (imageUrl) {
        // Convert legacy URLs to new format
        imageUrl = convertLegacyImageUrl(imageUrl);
        
        // Convert any small image URLs (m prefix) to full-size image URLs (f prefix)
        if (imageUrl.includes('/card/m')) {
          imageUrl = imageUrl.replace('/card/m', '/card/f');
        }
        
        // Use lower quality for preloading
        imagesToPreload.push(getProxiedImageUrl(imageUrl, 60, 300, 'webp'));
      }
    });
  }
  
  // For previous page, preload first 4 images only if we're not on the first page
  if (hasPrevPage) {
    const prevPagePlayers = players.slice(prevPageStart, prevPageStart + Math.min(4, pageSize));
    prevPagePlayers.forEach(player => {
      // Choose the most appropriate image
      let imageUrl = player.image_front || player.image || player.image_small;
      
      if (imageUrl) {
        // Convert legacy URLs to new format
        imageUrl = convertLegacyImageUrl(imageUrl);
        
        // Convert any small image URLs (m prefix) to full-size image URLs (f prefix)
        if (imageUrl.includes('/card/m')) {
          imageUrl = imageUrl.replace('/card/m', '/card/f');
        }
        
        // Use lower quality for preloading
        imagesToPreload.push(getProxiedImageUrl(imageUrl, 60, 300, 'webp'));
      }
    });
  }
  
  // Preload the images in the background
  if (imagesToPreload.length > 0) {
    // Using low-priority preloading to avoid competing with current page resources
    setTimeout(() => {
      imagesToPreload.forEach(url => {
        const img = new Image();
        img.src = url;
      });
    }, 300); // Slight delay to prioritize current page content
  }
}